import { Controller } from "@hotwired/stimulus"
import { isValidUrl } from "../../global/javascript/utils"
import { TooltipService } from "../services/tooltip_service"

export default class extends Controller {
  static targets = ["serverUrl", "dropdownTemplate", "inputTemplate", "optionTemplate", "dropdownOptionsList"]

  static values = {
    documentationId: String
  }

  connect() {
    this.tooltipService = new TooltipService()
    this.#initializeData()
    this.#initializeLocalStorage()
    this.#setupUI()
  }

  // ===== MAIN RENDERING ===== //
  renderUrlWithInputs() {
    // Parse server variables from URL (e.g., {var} in http://{var}.bump.sh)
    const regex = /\{(\w+)\}/g
    let html = this.currentServerRawUrl
    const matches = [...html.matchAll(regex)].map(match => match[1])

    // Replace each variable with its corresponding input/dropdown
    matches.forEach(varName => {
      const varValue = this.#getValueForServerVariable(varName)
      const description = this.currentServerValues[varName]?.description
      const allowedValues = this.currentServerValues[varName]?.allowed_values || []

      const variableHtml = this.#replaceTemplatePlaceholders(varName, varValue, description, allowedValues)
      html = html.replace(`{${varName}}`, variableHtml)
    })
    this.serverUrlTarget.innerHTML = html

    this.#setupAutoResize()
    this.#setupTooltips()
  }

  updateServerVariables(event = null) {
    if (event) {
      event.preventDefault()
    }

    const variableValues = this.#collectServerVariableValues()
    this.#setCurrentServerVariables(variableValues)

    const updatedUrl = this.#updateUrlWithVariables()
    this.#dispatchEvent(updatedUrl)
  }

  // ===== URL UPDATE ===== //
  #updateUrlWithVariables() {
    let url = this.currentServerRawUrl
    let serverVariablesWithValues = this.#getCurrentServerVariables()
    const regex = /\{(\w+)\}/g
    const matches = [...url.matchAll(regex)].map(match => match[1])

    matches.forEach(match => {
      if (serverVariablesWithValues[match] !== undefined) {
        url = url.replace(`{${match}}`, serverVariablesWithValues[match])
      }
    })
    return url
  }

  #dispatchEvent(url) {
    // NOTE: URL may be incorrect (for example if default is missing, so we avoid crashing dispatch)
    if (!isValidUrl(url)) {
      return
    }

    this.dispatch("updateUrl", { detail: { url: url } })
  }

  // ===== VALUES  ===== //
  #collectServerVariableValues() {
    const serverVariablesWithValues = {}
    this.serverUrlTarget.querySelectorAll(".server-variable-input, .server-variable-dropdown").forEach(input => {
      if (input.tagName === "INPUT") {
        // Pour les inputs, utiliser directement l'attribut name
        serverVariablesWithValues[input.name] = input.value
      } else if (input.classList.contains("server-variable-dropdown")) {
        const value = this.#extractDropdownValue(input)
        const varName = this.#extractVariableName(input)
        if (varName) {
          serverVariablesWithValues[varName] = value
        }
      }
    })

    return serverVariablesWithValues
  }

  #extractDropdownValue(dropdown) {
    const currentElement = dropdown.querySelector("[data-dropdown-target='current']")
    const selectedButton = dropdown.querySelector(".server-button.selected")
    return selectedButton ? selectedButton.dataset.value : currentElement.textContent.trim()
  }

  #extractVariableName(dropdown) {
    const currentElement = dropdown.querySelector("[data-dropdown-target='current']")
    return currentElement.getAttribute("data-variable-name")
  }

  // ===== LOCALSTORAGE ===== //
  #getAllServerVariables() {
    return JSON.parse(localStorage.getItem(this.localStorageName)) || {}
  }

  #setAllServerVariables(data) {
    localStorage.setItem(this.localStorageName, JSON.stringify(data))
  }

  #getCurrentServerVariables() {
    return this.#getAllServerVariables()[this.currentServerId] || {}
  }

  // ===== INITIALIZATION ===== //
  #initializeData() {
    this.localStorageName = `documentation:${this.documentationIdValue}:server-variables`
    this.currentServerValues = JSON.parse(this.serverUrlTarget.dataset.currentServerValues)
    this.currentServerRawUrl = this.serverUrlTarget.dataset.currentServerRawUrl
    this.currentServerId = this.serverUrlTarget.dataset.currentServerId // key in localStorage
  }

  #initializeLocalStorage() {
    const storedData = this.#getAllServerVariables()
    if (!storedData || typeof storedData !== "object") {
      this.#setAllServerVariables({})
    }
  }

  #setupUI() {
    this.renderUrlWithInputs()
    this.updateServerVariables()
  }

  #setCurrentServerVariables(serverVariables) {
    const allVariables = this.#getAllServerVariables()
    allVariables[this.currentServerId] = serverVariables
    this.#setAllServerVariables(allVariables)
  }

  // ===== HTML GENERATION ===== //
  #replaceTemplatePlaceholders(varName, varValue, description, allowedValues) {
    // Choose the appropriate template based on whether we have allowed values
    const template = allowedValues && allowedValues.length > 0
      ? this.dropdownTemplateTarget
      : this.inputTemplateTarget

    let html = template.innerHTML

    // Replace placeholders
    html = html.replace(/{var_name}/g, varName)
    html = html.replace(/{var_value}/g, varValue)
    html = html.replace(/{description}/g, description || "")

    // Create temporary element for DOM manipulation
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html

    // Generate dropdown options if needed
    if (allowedValues && allowedValues.length > 0) {
      this.#populateDropdownOptions(tempDiv, allowedValues, varValue)
    }

    return tempDiv.innerHTML
  }

  #populateDropdownOptions(tempDiv, allowedValues, selectedValue) {
    const dropdownOptionsList = tempDiv.querySelector("[data-explorer-server-variables-target='dropdown-options-list']")
    if (!dropdownOptionsList) return

    const optionTemplate = this.optionTemplateTarget
    if (!optionTemplate) return

    const optionsHtml = allowedValues.map(value => {
      const isSelected = selectedValue === value
      let templateHtml = optionTemplate.innerHTML

      // Replace placeholders
      templateHtml = templateHtml.replace(/{value}/g, value)
      templateHtml = templateHtml.replace(/{selected_class}/g, isSelected ? "selected" : "")
      templateHtml = templateHtml.replace(/{checkmark_hidden_class}/g, isSelected ? "" : "hidden")

      return templateHtml
    }).join("")

    dropdownOptionsList.innerHTML = optionsHtml
  }

  #getValueForServerVariable(varName) {
    const localStorageValues = this.#getCurrentServerVariables()
    return localStorageValues[varName] || this.currentServerValues[varName]?.default || this.currentServerValues[varName]?.allowed_values[0] || varName
  }

  updateDropdownValue(event) {
    const button = event.currentTarget
    const dropdown = button.closest(".server-variable-dropdown")

    this.#updateDropdownSelection(dropdown, button)
    this.#updateDropdownDisplay(dropdown, button.dataset.value)
    this.#updateDropdownCheckmarks(dropdown, button)

    this.updateServerVariables()
  }

  // ===== RESIZE ===== //
  #setupAutoResize() {
    this.serverUrlTarget.querySelectorAll(".server-variable-input").forEach(input => {
      input.addEventListener("input", () => this.#autoResizeInput(input))
      this.#autoResizeInput(input)
    })
  }

  #autoResizeInput(input) {
    // Measure text width and set explicit width
    const textWidth = this.#measureTextWidth(input)
    const finalWidth = Math.min(Math.max(textWidth, 48), 256)

    input.style.width = finalWidth + "px"

    // Only show ellipsis if content exceeds max width
    const needsEllipsis = textWidth > 256
    input.style.textOverflow = needsEllipsis ? "ellipsis" : "clip"
    input.style.overflow = needsEllipsis ? "hidden" : "visible"
    input.style.whiteSpace = needsEllipsis ? "nowrap" : "normal"
  }

  #measureTextWidth(input) {
    // Create temporary span with same font properties
    // Inputs have fixed width (150px) inherited from parent CSS,
    // but we want them to adapt to content size (e.g., "demo" = 56px).
    // `width: fit-content` can't override inherited fixed width.
    // We can measure exact text width and force it with `style.width`.
    const temp = document.createElement("span")
    const style = window.getComputedStyle(input)

    temp.style.font = style.font
    temp.textContent = input.value || ""

    document.body.appendChild(temp)
    const textWidth = temp.offsetWidth
    document.body.removeChild(temp)

    const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)

    return textWidth + padding + 2 // Small safety margin
  }

  // ===== DROPDOWN UPDATE ===== //
  #updateDropdownSelection(dropdown, selectedButton) {
    dropdown.querySelectorAll(".server-button").forEach(option => {
      option.classList.remove("selected")
    })
    selectedButton.classList.add("selected")
  }

  #updateDropdownDisplay(dropdown, value) {
    const currentElement = dropdown.querySelector("[data-dropdown-target='current']")
    currentElement.textContent = value
  }

  #updateDropdownCheckmarks(dropdown, selectedButton) {
    // Remove all checkmarks first
    dropdown.querySelectorAll(".checkmark").forEach(checkmark => {
      checkmark.classList.add("hidden")
    })

    // Show checkmark for selected button
    if (selectedButton.classList.contains("selected")) {
      const checkmark = selectedButton.querySelector(".checkmark")
      if (checkmark) {
        checkmark.classList.remove("hidden")
      }
    }
  }

  // ===== TOOLTIP ===== //
  #setupTooltips() {
    this.serverUrlTarget.querySelectorAll(".server-variable-input, .server-variable-dropdown").forEach(input => {
      const tooltip = input.parentElement.querySelector(".server-variable-tooltip")
      if (!tooltip) return

      // Only set up tooltip if it has content (not empty description)
      const tooltipContent = tooltip.textContent.trim()
      if (tooltipContent && tooltipContent !== "") {
        this.tooltipService.setupTooltip(input, tooltip)
      }
    })
  }
}
